import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { FileDown, Truck, Ban, Loader2, Undo2, Camera } from 'lucide-react';

const CANCELLABLE_STATUSES = ['pending', 'paid'];
const RETURN_REASONS = [
  { value: 'damaged', label: 'Item arrived damaged' },
  { value: 'wrong_item', label: 'Wrong item delivered' },
  { value: 'quality', label: 'Quality / freshness issue' },
  { value: 'not_as_described', label: 'Not as described' },
  { value: 'expired', label: 'Product near / past expiry' },
  { value: 'other', label: 'Other (explain below)' },
];

const formatReturnStatus = (s) => {
  if (!s || s === 'none') return null;
  return s.replace('_', ' ');
};

const returnBadgeClass = (s) => ({
  requested: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  picked_up: 'bg-indigo-100 text-indigo-800',
  received: 'bg-purple-100 text-purple-800',
  refunded: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
}[s] || 'bg-gray-100 text-gray-800');

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [returnPhoto, setReturnPhoto] = useState(null);
  const [submittingReturn, setSubmittingReturn] = useState(false);

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, { withCredentials: true });
      setOrders(data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoice/${orderId}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const performCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${cancelTarget.order_id}/customer-cancel`,
        {},
        { withCredentials: true }
      );
      toast.success(data.refund_pending
        ? 'Order cancelled. Refund will be processed within 15 days.'
        : 'Order cancelled successfully.');
      setCancelTarget(null);
      await fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const openReturnDialog = (order) => {
    setReturnTarget(order);
    setReturnReason('');
    setReturnNotes('');
    setReturnPhoto(null);
  };

  const closeReturnDialog = () => {
    if (submittingReturn) return;
    setReturnTarget(null);
    setReturnReason('');
    setReturnNotes('');
    setReturnPhoto(null);
  };

  const submitReturn = async () => {
    if (!returnTarget) return;
    if (!returnReason) {
      toast.error('Please choose a reason for the return.');
      return;
    }
    if (!returnPhoto) {
      toast.error('A clear photo of the issue is required.');
      return;
    }
    if (returnPhoto.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5 MB.');
      return;
    }
    const reasonLabel = RETURN_REASONS.find(r => r.value === returnReason)?.label || returnReason;
    const composedReason = returnNotes.trim()
      ? `${reasonLabel} — ${returnNotes.trim()}`
      : reasonLabel;
    const fd = new FormData();
    fd.append('reason', composedReason);
    fd.append('photo', returnPhoto);
    setSubmittingReturn(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${returnTarget.order_id}/request-return`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Return request submitted. We will review it shortly.');
      setReturnTarget(null);
      await fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit return request');
    } finally {
      setSubmittingReturn(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />

      <div className="container mx-auto px-4 py-12 pt-20">
        <h1 className="text-4xl font-black font-heading mb-8" data-testid="orders-heading">My Orders</h1>

        {orders.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground" data-testid="no-orders-message">No orders yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => {
              const returnStatusLabel = formatReturnStatus(order.return_status);
              const canRequestReturn = order.status === 'delivered' && (!order.return_status || order.return_status === 'none');
              return (
                <div key={order.order_id} className="p-6 rounded-2xl bg-white border border-[#E0E0E0]" data-testid={`order-${order.order_id}`}>
                  <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-4">
                    <div>
                      <h3 className="font-bold text-lg font-heading" data-testid={`order-id-${order.order_id}`}>
                        Order #{order.order_id}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })} IST
                      </p>
                      {order.tracking_id && (
                        <p className="text-sm text-primary mt-1 flex items-center gap-1">
                          <Truck className="h-3 w-3" /> Tracking: {order.tracking_id}
                        </p>
                      )}
                      {returnStatusLabel && (
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold mt-2 ${returnBadgeClass(order.return_status)}`}
                          data-testid={`return-status-${order.order_id}`}
                        >
                          Return: {returnStatusLabel}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="text-right">
                        <p className="text-xl font-bold text-primary" data-testid={`order-total-${order.order_id}`}>
                          {'\u20B9'}{order.total_amount.toFixed(2)}
                        </p>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-1 ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                          order.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'refunded' ? 'bg-red-100 text-red-800' :
                          order.status === 'rto' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      <Button variant="outline" size="sm" className="rounded-full" onClick={() => downloadInvoice(order.order_id)} data-testid={`download-invoice-${order.order_id}`}>
                        <FileDown className="h-4 w-4 mr-1" /> Invoice
                      </Button>
                      {CANCELLABLE_STATUSES.includes(order.status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={() => setCancelTarget(order)}
                          data-testid={`cancel-order-${order.order_id}`}
                        >
                          <Ban className="h-4 w-4 mr-1" /> Cancel Order
                        </Button>
                      )}
                      {canRequestReturn && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full text-amber-700 border-amber-200 hover:bg-amber-50"
                          onClick={() => openReturnDialog(order)}
                          data-testid={`request-return-${order.order_id}`}
                        >
                          <Undo2 className="h-4 w-4 mr-1" /> Request Return
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex gap-4 text-sm" data-testid={`order-item-${order.order_id}-${index}`}>
                        <div className="flex-1">
                          <p className="font-medium">{item.name}</p>
                          <p className="text-muted-foreground">Qty: {item.quantity} x {'\u20B9'}{item.price.toFixed(2)}</p>
                        </div>
                        <p className="font-medium">{'\u20B9'}{(item.quantity * item.price).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />

      <AlertDialog open={!!cancelTarget} onOpenChange={(open) => { if (!open && !cancelling) setCancelTarget(null); }}>
        <AlertDialogContent data-testid="cancel-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Cancel this order?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {cancelTarget && (
                  <div className="rounded-lg border border-red-100 bg-red-50/40 p-3">
                    <div className="font-semibold text-foreground">Order #{cancelTarget.order_id}</div>
                    <div className="text-muted-foreground">{'\u20B9'}{Number(cancelTarget.total_amount || 0).toFixed(2)} · status: <b>{cancelTarget.status}</b></div>
                  </div>
                )}
                <p>You can cancel this order as it has not been shipped yet. Do you wish to proceed?</p>
                {cancelTarget?.status === 'paid' && (
                  <p className="text-amber-700"><b>Note:</b> Since this order has already been paid, your refund will be processed within 15 days from the date of cancellation.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling} data-testid="cancel-order-back">Keep Order</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); performCancel(); }}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="cancel-order-confirm"
            >
              {cancelling ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Cancelling…</>) : 'Yes, Cancel Order'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!returnTarget} onOpenChange={(open) => { if (!open) closeReturnDialog(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="return-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Request a return</DialogTitle>
            <DialogDescription>
              Tell us what went wrong with order <b>#{returnTarget?.order_id}</b>. A clear photo of the issue is required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="return-reason">Reason <span className="text-red-600">*</span></Label>
              <Select value={returnReason} onValueChange={setReturnReason}>
                <SelectTrigger id="return-reason" data-testid="return-reason-select">
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent>
                  {RETURN_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} data-testid={`return-reason-${r.value}`}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="return-notes">Additional details (optional)</Label>
              <Textarea
                id="return-notes"
                placeholder="Describe the issue in a few words…"
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                rows={3}
                maxLength={500}
                data-testid="return-notes-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="return-photo">Photo of the item / issue <span className="text-red-600">*</span></Label>
              <div className="flex items-center gap-3">
                <label
                  htmlFor="return-photo"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[#C7C7C7] bg-[#FAFAFA] text-sm cursor-pointer hover:border-primary hover:bg-white"
                >
                  <Camera className="h-4 w-4" />
                  {returnPhoto ? 'Change photo' : 'Choose photo'}
                </label>
                <input
                  id="return-photo"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => setReturnPhoto(e.target.files?.[0] || null)}
                  data-testid="return-photo-input"
                />
                {returnPhoto && (
                  <span className="text-xs text-muted-foreground truncate max-w-[180px]" data-testid="return-photo-name">
                    {returnPhoto.name} · {(returnPhoto.size / 1024).toFixed(0)} KB
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">PNG, JPG, WEBP or GIF · max 5 MB.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeReturnDialog} disabled={submittingReturn} data-testid="return-cancel">
              Cancel
            </Button>
            <Button
              onClick={submitReturn}
              disabled={submittingReturn || !returnReason || !returnPhoto}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="return-submit"
            >
              {submittingReturn ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Submitting…</>) : 'Submit return request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
